import { ToastProvider } from '@openeducation/oe-it-components';
import {
  EnvironmentProvider,
  LoginProvider,
  NotificationProvider,
  useLoginContext,
} from '@openeducation/oe-it-providers';
import {
  AuthenticationService,
  AxiosInterceptor,
  oeSsoTeacherCookieStorage,
  Environment,
  isTeacherProfile,
  PersonProfileService,
  teacherPortalAuthentication,
  getPersonIdFromToken,
} from '@openeducation/oe-it-services';
import { useDesktopNotification } from '@openeducation/oe-it-teacher-portal';
import { OeDefaultTheme, ThemeProvider } from '@openeducation/oe-it-themes';
import { Provider as AtomProvider } from 'jotai';
import React from 'react';
import { QueryClient } from 'react-query';

const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false, refetchIntervalInBackground: true } },
});

interface AppProvidersProps {
  env: Environment;
  children?: React.ReactNode;
}

export const AppProviders: React.FC<AppProvidersProps> = ({ children, env }) => {
  const { logout } = useLoginContext();

  React.useEffect(() => {
    if (env && oeSsoTeacherCookieStorage) {
      AxiosInterceptor(oeSsoTeacherCookieStorage, env, () => {
        logout();
      });
    }
  }, [env, logout]);

  async function validateAuthorization(id?: number | string) {
    if (!id) {
      return Promise.resolve(false);
    }
    const profile = await PersonProfileService.getProfileById(env, id as number);
    return isTeacherProfile(profile);
  }

  const { showNotification } = useDesktopNotification();

  return (
    <AtomProvider>
      <EnvironmentProvider env={env}>
        <ThemeProvider theme={OeDefaultTheme}>
          <ToastProvider>
            <LoginProvider
              queryClient={queryClient}
              authenticateWithUsernameAndPassword={
                AuthenticationService.authenticateWithUsernameAndPassword
              }
              resetUserPassword={AuthenticationService.resetUserPassword}
              getIdFromToken={getPersonIdFromToken}
              validateAuthentication={teacherPortalAuthentication}
              validateAuthorization={validateAuthorization}
              authDataStorage={oeSsoTeacherCookieStorage}
            >
              <NotificationProvider showNotification={showNotification}>
                {children}
              </NotificationProvider>
            </LoginProvider>
          </ToastProvider>
        </ThemeProvider>
      </EnvironmentProvider>
    </AtomProvider>
  );
};
